import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api';

// ─────────────────────────────────────────────────────────────────────────────
// Shared style helpers
// ─────────────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: '#1A1F2E',
  border: '1px solid #252B38',
  borderRadius: '16px',
  padding: '28px 24px',
  marginBottom: '24px',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 800,
  color: '#F0F6FC',
  marginBottom: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const subtitle: React.CSSProperties = {
  fontSize: '14px',
  color: '#8892A4',
  marginBottom: '20px',
};

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #F97316, #EA580C)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '12px 22px',
  fontSize: '15px',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const btnSecondary: React.CSSProperties = {
  background: '#252B38',
  color: '#F0F6FC',
  border: '1px solid #353D4F',
  borderRadius: '10px',
  padding: '11px 20px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const btnDanger: React.CSSProperties = {
  background: 'rgba(244,67,54,0.12)',
  color: '#F44336',
  border: '1px solid rgba(244,67,54,0.25)',
  borderRadius: '8px',
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = {
  background: '#141820',
  border: '1px solid #252B38',
  borderRadius: '8px',
  color: '#F0F6FC',
  padding: '9px 12px',
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle };

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  fontSize: '14px',
};

const thStyle: React.CSSProperties = {
  background: '#141820',
  color: '#F97316',
  fontWeight: 700,
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  borderBottom: '1px solid #252B38',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #252B38',
  color: '#F0F6FC',
  verticalAlign: 'middle',
};

// ─────────────────────────────────────────────────────────────────────────────
// Image compression / HEIC → JPEG conversion
// Runs entirely in the browser before upload:
//   • Converts HEIC/HEIF (iOS gallery) to JPEG — Gemini doesn't support HEIC
//   • Resizes to ≤1920px and compresses to ~85% JPEG quality
//   • Smaller file → faster Gemini response → avoids nginx 60s proxy timeout
// ─────────────────────────────────────────────────────────────────────────────
async function toJpeg(file: File): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 1920;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > MAX || h > MAX) {
        if (w >= h) { h = Math.round(h * MAX / w); w = MAX; }
        else        { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const name = file.name.replace(/\.[^.]+$/, '.jpg');
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        0.85,
      );
    };

    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable camera/file upload button
// ─────────────────────────────────────────────────────────────────────────────
function CameraUpload({ onFile, disabled }: { onFile: (f: File) => void; disabled?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      {/* Camera — opens native camera on mobile */}
      <button style={{ ...btnPrimary, opacity: disabled ? 0.5 : 1 }} disabled={disabled}
        onClick={() => { if (inputRef.current) { inputRef.current.capture = 'environment'; inputRef.current.click(); } }}>
        📷 Take Photo
      </button>
      {/* Gallery / file picker */}
      <button style={{ ...btnSecondary, opacity: disabled ? 0.5 : 1 }} disabled={disabled}
        onClick={() => { if (inputRef.current) { inputRef.current.removeAttribute('capture'); inputRef.current.click(); } }}>
        🖼️ Upload Image
      </button>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status pill
// ─────────────────────────────────────────────────────────────────────────────
function StatusPill({ msg, type }: { msg: string; type: 'success' | 'error' | 'info' }) {
  const bg = type === 'success' ? 'rgba(34,197,94,0.12)' : type === 'error' ? 'rgba(244,67,54,0.12)' : 'rgba(249,115,22,0.12)';
  const col = type === 'success' ? '#22C55E' : type === 'error' ? '#F44336' : '#F97316';
  return (
    <div style={{ background: bg, border: `1px solid ${col}33`, borderRadius: '8px', padding: '10px 14px', color: col, fontSize: '14px', fontWeight: 600, marginTop: '14px' }}>
      {msg}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Roster Import
// ─────────────────────────────────────────────────────────────────────────────
interface RosterPlayer { name: string; number: number | null; position: string | null; grade: string | null; }

function RosterImport() {
  const [teams, setTeams]           = useState<any[]>([]);
  const [teamId, setTeamId]         = useState('');
  const [preview, setPreview]       = useState<string | null>(null);
  const [parsing, setParsing]       = useState(false);
  const [players, setPlayers]       = useState<RosterPlayer[]>([]);
  const [saving, setSaving]         = useState(false);
  const [status, setStatus]         = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    api.get('/teams').then(r => {
      setTeams(r.data);
      if (r.data.length > 0) setTeamId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const handleFile = useCallback(async (file: File) => {
    setStatus(null);
    setPlayers([]);
    setParsing(true);

    // Convert to JPEG before preview so the preview matches what we send
    const compressed = await toJpeg(file);
    setPreview(URL.createObjectURL(compressed));

    const form = new FormData();
    form.append('image', compressed);
    form.append('team_id', teamId);

    try {
      const { data } = await api.post('/ai/parse-roster', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000, // 120s — Gemini vision can be slow on complex screenshots
      });
      setPlayers(data.players || []);
      if ((data.players || []).length === 0) {
        setStatus({ msg: 'No players detected — try a clearer photo.', type: 'error' });
      }
    } catch (err: any) {
      const msg = err.code === 'ECONNABORTED'
        ? 'Request timed out — the image may be too large. Try again.'
        : err.response?.data?.error || 'Parsing failed. Try again.';
      setStatus({ msg, type: 'error' });
    } finally {
      setParsing(false);
    }
  }, [teamId]);

  const updatePlayer = (i: number, field: keyof RosterPlayer, value: any) => {
    setPlayers(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  };

  const removePlayer = (i: number) => setPlayers(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!teamId || players.length === 0) return;
    setSaving(true);
    setStatus(null);
    try {
      const { data } = await api.post('/ai/save-roster', { team_id: teamId, players });
      setStatus({ msg: `✅ ${data.count} player${data.count !== 1 ? 's' : ''} added to the roster!`, type: 'success' });
      setPlayers([]);
      setPreview(null);
    } catch (err: any) {
      setStatus({ msg: err.response?.data?.error || 'Save failed.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedTeam = teams.find(t => t.id === teamId);

  return (
    <div style={card}>
      <div style={sectionTitle}>🤖 Import Roster from Photo</div>
      <div style={subtitle}>Take a photo of a printed roster sheet. AI will extract every player automatically.</div>

      {/* Team selector */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '13px', color: '#8892A4', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
          Select Team
        </label>
        <select style={{ ...selectStyle, maxWidth: '360px' }} value={teamId} onChange={e => setTeamId(e.target.value)}>
          {teams.map(t => (
            <option key={t.id} value={t.id}>{t.name} {t.season_year ? `(${t.season_year})` : ''}</option>
          ))}
        </select>
      </div>

      {/* Camera / upload */}
      <CameraUpload onFile={handleFile} disabled={!teamId || parsing} />

      {/* Photo preview */}
      {preview && (
        <div style={{ marginTop: '16px' }}>
          <img src={preview} alt="Roster preview"
            style={{ maxWidth: '100%', maxHeight: '280px', borderRadius: '10px', border: '1px solid #252B38', objectFit: 'contain' }} />
        </div>
      )}

      {/* Parsing spinner */}
      {parsing && (
        <div style={{ marginTop: '16px', color: '#F97316', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: 18, height: 18, border: '3px solid #F9731640', borderTop: '3px solid #F97316', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          AI is reading your roster…
        </div>
      )}

      {/* Editable results table */}
      {players.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '14px', color: '#8892A4', marginBottom: '10px', fontWeight: 600 }}>
            Found {players.length} player{players.length !== 1 ? 's' : ''} — review and edit before saving to <strong style={{ color: '#F0F6FC' }}>{selectedTeam?.name}</strong>:
          </div>
          <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #252B38' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Name</th>
                  <th style={{ ...thStyle, width: '80px' }}>Jersey</th>
                  <th style={{ ...thStyle, width: '120px' }}>Position</th>
                  <th style={{ ...thStyle, width: '80px' }}>Grade</th>
                  <th style={{ ...thStyle, width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#141820' : 'transparent' }}>
                    <td style={{ ...tdStyle, color: '#8892A4', fontWeight: 700 }}>{i + 1}</td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} value={p.name}
                        onChange={e => updatePlayer(i, 'name', e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} type="number"
                        value={p.number ?? ''} placeholder="—"
                        onChange={e => updatePlayer(i, 'number', e.target.value ? parseInt(e.target.value) : null)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} value={p.position ?? ''}
                        placeholder="—" onChange={e => updatePlayer(i, 'position', e.target.value || null)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} value={p.grade ?? ''}
                        placeholder="—" onChange={e => updatePlayer(i, 'grade', e.target.value || null)} />
                    </td>
                    <td style={tdStyle}>
                      <button style={btnDanger} onClick={() => removePlayer(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving…' : `💾 Add ${players.length} Players to Roster`}
            </button>
            <button style={btnSecondary} onClick={() => { setPlayers([]); setPreview(null); setStatus(null); }}>
              ✕ Clear
            </button>
          </div>
        </div>
      )}

      {status && <StatusPill msg={status.msg} type={status.type} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Lineup Import
// ─────────────────────────────────────────────────────────────────────────────
interface LineupBatter { order: number; name: string; number: string; position: string; }

function LineupImport() {
  const [games, setGames]           = useState<any[]>([]);
  const [gameId, setGameId]         = useState('');
  const [teamSide, setTeamSide]     = useState<'home' | 'away'>('home');
  const [preview, setPreview]       = useState<string | null>(null);
  const [parsing, setParsing]       = useState(false);
  const [lineup, setLineup]         = useState<LineupBatter[]>([]);
  const [saving, setSaving]         = useState(false);
  const [status, setStatus]         = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [savedLineups, setSavedLineups] = useState<{ lineup_home: LineupBatter[]; lineup_away: LineupBatter[] } | null>(null);

  useEffect(() => {
    api.get('/games').then(r => {
      // Only show baseball / softball games
      const bb = r.data.filter((g: any) => ['baseball', 'softball'].includes(g.sport));
      setGames(bb);
      if (bb.length > 0) setGameId(bb[0].id);
    }).catch(() => {});
  }, []);

  // Load existing lineups whenever game changes
  useEffect(() => {
    if (!gameId) return;
    api.get(`/ai/lineup/${gameId}`).then(r => setSavedLineups(r.data)).catch(() => setSavedLineups(null));
  }, [gameId]);

  const handleFile = useCallback(async (file: File) => {
    setStatus(null);
    setLineup([]);
    setParsing(true);

    const compressed = await toJpeg(file);
    setPreview(URL.createObjectURL(compressed));

    const form = new FormData();
    form.append('image', compressed);
    form.append('game_id', gameId);
    form.append('team', teamSide);

    try {
      const { data } = await api.post('/ai/parse-lineup', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
      const sorted = (data.lineup || []).sort((a: LineupBatter, b: LineupBatter) => a.order - b.order);
      setLineup(sorted);
      if (sorted.length === 0) {
        setStatus({ msg: 'No batters detected — try a clearer photo.', type: 'error' });
      }
    } catch (err: any) {
      const msg = err.code === 'ECONNABORTED'
        ? 'Request timed out — the image may be too large. Try again.'
        : err.response?.data?.error || 'Parsing failed. Try again.';
      setStatus({ msg, type: 'error' });
    } finally {
      setParsing(false);
    }
  }, [gameId, teamSide]);

  const updateBatter = (i: number, field: keyof LineupBatter, value: any) => {
    setLineup(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  };

  const removeBatter = (i: number) => setLineup(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!gameId || lineup.length === 0) return;
    setSaving(true);
    setStatus(null);
    try {
      await api.post('/ai/save-lineup', { game_id: gameId, team: teamSide, lineup });
      setStatus({ msg: `✅ ${teamSide === 'home' ? 'Home' : 'Away'} lineup saved (${lineup.length} batters)!`, type: 'success' });
      // Refresh saved lineups display
      const r = await api.get(`/ai/lineup/${gameId}`);
      setSavedLineups(r.data);
      setLineup([]);
      setPreview(null);
    } catch (err: any) {
      setStatus({ msg: err.response?.data?.error || 'Save failed.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedGame = games.find(g => g.id === gameId);
  const POSITIONS = ['', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'EH', 'PH', 'PR'];

  const LineupTable = ({ batters, label }: { batters: LineupBatter[]; label: string }) => (
    <div style={{ flex: 1, minWidth: '260px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: '#8892A4', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</div>
      {batters.length === 0
        ? <div style={{ color: '#8892A4', fontSize: '13px', padding: '12px 0' }}>No lineup set</div>
        : (
          <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #252B38' }}>
            {batters.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: i % 2 === 0 ? '#141820' : 'transparent', borderBottom: i < batters.length - 1 ? '1px solid #252B38' : 'none' }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#F97316', color: '#fff', fontSize: '11px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{b.order}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: '13px' }}>{b.name}</span>
                {b.number && <span style={{ fontSize: '12px', color: '#8892A4' }}>#{b.number}</span>}
                {b.position && <span style={{ fontSize: '11px', color: '#F97316', fontWeight: 700, background: 'rgba(249,115,22,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{b.position}</span>}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );

  return (
    <div style={card}>
      <div style={sectionTitle}>📋 Set Batting Lineup from Photo</div>
      <div style={subtitle}>Take a photo of a lineup card. AI reads the batting order for home or away team.</div>

      {/* Game + side selectors */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ flex: 2, minWidth: '200px' }}>
          <label style={{ fontSize: '13px', color: '#8892A4', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Game</label>
          <select style={selectStyle} value={gameId} onChange={e => setGameId(e.target.value)}>
            {games.length === 0 && <option value="">No baseball/softball games</option>}
            {games.map(g => (
              <option key={g.id} value={g.id}>
                {g.home_team} vs {g.away_team} — {new Date(g.scheduled_at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: '160px' }}>
          <label style={{ fontSize: '13px', color: '#8892A4', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Team</label>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #252B38' }}>
            {(['home', 'away'] as const).map(side => (
              <button key={side} onClick={() => setTeamSide(side)} style={{
                flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 700,
                background: teamSide === side ? '#F97316' : '#141820',
                color: teamSide === side ? '#fff' : '#8892A4',
                transition: 'all 0.15s',
              }}>
                {side === 'home' ? (selectedGame?.home_team || 'Home') : (selectedGame?.away_team || 'Away')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Camera / upload */}
      <CameraUpload onFile={handleFile} disabled={!gameId || parsing} />

      {/* Photo preview */}
      {preview && (
        <div style={{ marginTop: '16px' }}>
          <img src={preview} alt="Lineup card preview"
            style={{ maxWidth: '100%', maxHeight: '280px', borderRadius: '10px', border: '1px solid #252B38', objectFit: 'contain' }} />
        </div>
      )}

      {/* Parsing spinner */}
      {parsing && (
        <div style={{ marginTop: '16px', color: '#F97316', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: 18, height: 18, border: '3px solid #F9731640', borderTop: '3px solid #F97316', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          AI is reading your lineup card…
        </div>
      )}

      {/* Editable lineup */}
      {lineup.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '14px', color: '#8892A4', marginBottom: '10px', fontWeight: 600 }}>
            Found {lineup.length} batter{lineup.length !== 1 ? 's' : ''} — review and edit the{' '}
            <strong style={{ color: '#F0F6FC' }}>{teamSide === 'home' ? selectedGame?.home_team : selectedGame?.away_team}</strong> batting order:
          </div>
          <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #252B38' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '52px' }}>Order</th>
                  <th style={thStyle}>Batter</th>
                  <th style={{ ...thStyle, width: '80px' }}>Jersey</th>
                  <th style={{ ...thStyle, width: '110px' }}>Position</th>
                  <th style={{ ...thStyle, width: '52px' }}></th>
                </tr>
              </thead>
              <tbody>
                {lineup.map((b, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#141820' : 'transparent' }}>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#F97316', color: '#fff', fontSize: '13px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {b.order}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} value={b.name}
                        onChange={e => updateBatter(i, 'name', e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={{ ...inputStyle, padding: '7px 10px' }} value={b.number}
                        placeholder="—" onChange={e => updateBatter(i, 'number', e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <select style={{ ...inputStyle, padding: '7px 10px' }} value={b.position}
                        onChange={e => updateBatter(i, 'position', e.target.value)}>
                        {POSITIONS.map(p => <option key={p} value={p}>{p || '— pos —'}</option>)}
                        {!POSITIONS.includes(b.position) && b.position && <option value={b.position}>{b.position}</option>}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <button style={btnDanger} onClick={() => removeBatter(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving…' : `💾 Save ${teamSide === 'home' ? 'Home' : 'Away'} Lineup`}
            </button>
            <button style={btnSecondary} onClick={() => { setLineup([]); setPreview(null); setStatus(null); }}>
              ✕ Clear
            </button>
          </div>
        </div>
      )}

      {status && <StatusPill msg={status.msg} type={status.type} />}

      {/* Saved lineups side-by-side */}
      {savedLineups && (savedLineups.lineup_home.length > 0 || savedLineups.lineup_away.length > 0) && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#F0F6FC', marginBottom: '14px', borderTop: '1px solid #252B38', paddingTop: '20px' }}>
            Current Saved Lineups
          </div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <LineupTable batters={savedLineups.lineup_home} label={selectedGame?.home_team || 'Home'} />
            <LineupTable batters={savedLineups.lineup_away} label={selectedGame?.away_team || 'Away'} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function AITools() {
  return (
    <div className="container" style={{ maxWidth: '860px', padding: '32px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 900, color: '#F0F6FC', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          🤖 AI Tools
        </h1>
        <p style={{ color: '#8892A4', fontSize: '15px', margin: '8px 0 0' }}>
          Use your phone camera to instantly import rosters and batting lineups — no typing required.
        </p>
      </div>

      <RosterImport />
      <LineupImport />

      {/* Keyframe for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
